import React from 'react';

/**
 * Composant pour afficher le contenu d'impression des procès-verbaux - Version ultra-compacte
 */
const PrintContentProcesVerbal = ({ procesVerbal }) => {
  const getAllEtudiants = (soutenance) => {
    if (!soutenance) return [];
    
    const etudiants = [];
    
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      soutenance.etudiants_soutenance.forEach(etudiantSoutenance => {
        if (etudiantSoutenance.etudiant) {
          etudiants.push(etudiantSoutenance.etudiant);
        }
      });
    } else if (soutenance.etudiant_principal_info) {
      etudiants.push(soutenance.etudiant_principal_info);
    }
    
    return etudiants;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Non définie';
    return timeString.slice(0, 5);
  };

  const getMentionLabel = (mention) => {
    const mentionLabels = {
      'AJOURNE': 'Ajourné',
      'PASSABLE': 'Passable',
      'ASSEZ_BIEN': 'Assez bien',
      'BIEN': 'Bien',
      'TRES_BIEN': 'Très bien',
      'EXCELLENT': 'Excellent'
    };
    return mentionLabels[mention] || mention || 'Non attribuée';
  };

  const getDecisionLabel = (decision) => {
    const decisionLabels = {
      'ADMIS': 'Admis',
      'ADMIS_RESERVE': 'Admis avec réserve',
      'AJOURNE': 'Ajourné',
      'REPORTE': 'Reporté'
    };
    return decisionLabels[decision] || decision || 'En attente';
  };

  const getJuryInfo = (soutenance) => {
    if (!soutenance?.jury) return null;
    
    const jury = soutenance.jury;
    const president = jury.president_interne || jury.president_externe;
    
    // Récupérer les membres du jury par rôle
    const rapporteur = jury.membres_jury?.find(m => m.role === 'RAPPORTEUR');
    const examinateur = jury.membres_jury?.find(m => m.role === 'EXAMINATEUR');
    
    return {
      president: president ? {
        nom: president.nom || president.utilisateur?.last_name,
        prenom: president.prenom || president.utilisateur?.first_name
      } : null,
      rapporteur: rapporteur ? {
        nom: rapporteur.enseignant_interne?.nom || rapporteur.enseignant_externe?.utilisateur?.last_name,
        prenom: rapporteur.enseignant_interne?.prenom || rapporteur.enseignant_externe?.utilisateur?.first_name
      } : null,
      examinateur: examinateur ? {
        nom: examinateur.enseignant_interne?.nom || examinateur.enseignant_externe?.utilisateur?.last_name,
        prenom: examinateur.enseignant_interne?.prenom || examinateur.enseignant_externe?.utilisateur?.first_name
      } : null
    };
  };

  const etudiants = getAllEtudiants(procesVerbal?.soutenance);
  const soutenance = procesVerbal?.soutenance;
  const juryInfo = getJuryInfo(soutenance);

  return (
    <div className="hidden print:block">
      {/* Informations générales - Ultra compact */}
      <table className="w-full border-collapse mb-2" style={{fontSize: '10pt'}}>
        <thead>
          <tr style={{backgroundColor: '#e5e5e5'}}>
            <th className="border border-black px-1 py-1 font-bold text-left" colSpan="4">
              INFORMATIONS GÉNÉRALES
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-black px-1 py-1 font-bold" style={{width: '20%'}}>Candidat(s)</td>
            <td className="border border-black px-1 py-1" colSpan="3">
              {etudiants.length > 0 ? (
                etudiants.map((etudiant, index) => (
                  <span key={index}>
                    <strong>{etudiant.nom} {etudiant.prenom}</strong>
                    {etudiant.matricule && <span> ({etudiant.matricule})</span>}
                    {index < etudiants.length - 1 && <br />}
                  </span>
                ))
              ) : (
                'Non spécifié'
              )}
            </td>
          </tr>
          <tr>
            <td className="border border-black px-1 py-1 font-bold">Date de soutenance</td>
            <td className="border border-black px-1 py-1" style={{width: '25%'}}>
              {formatDate(soutenance?.date_soutenance)}
            </td>
            <td className="border border-black px-1 py-1 font-bold" style={{width: '15%'}}>Heure</td>
            <td className="border border-black px-1 py-1">
              {formatTime(procesVerbal?.heure_debut)} - {formatTime(procesVerbal?.heure_fin)}
            </td>
          </tr>
          <tr>
            <td className="border border-black px-1 py-1 font-bold">Lieu</td>
            <td className="border border-black px-1 py-1">
              {soutenance?.salle?.nom || 'Non spécifié'}
            </td>
            <td className="border border-black px-1 py-1 font-bold">Type</td>
            <td className="border border-black px-1 py-1">
              {soutenance?.type_soutenance?.nom || 'Non spécifié'}
            </td>
          </tr>
          <tr>
            <td className="border border-black px-1 py-1 font-bold">Titre du travail</td>
            <td className="border border-black px-1 py-1" colSpan="3">
              {soutenance?.titre_travail || 'Non spécifié'}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Composition du jury - Ultra compact */}
      <table className="w-full border-collapse mb-2" style={{fontSize: '10pt'}}>
        <thead>
          <tr style={{backgroundColor: '#e5e5e5'}}>
            <th className="border border-black px-1 py-1 font-bold text-left" colSpan="3">
              COMPOSITION DU JURY
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-black px-1 py-1 font-bold" style={{width: '25%'}}>Président</td>
            <td className="border border-black px-1 py-1" style={{width: '50%'}}>
              {juryInfo?.president ? 
                `${juryInfo.president.prenom} ${juryInfo.president.nom}` : 
                'Non assigné'
              }
            </td>
            <td className="border border-black px-1 py-1 text-center" style={{width: '25%'}}>
              {procesVerbal?.date_signature_president ? 
                `Signé le ${formatDate(procesVerbal.date_signature_president)}` : 
                'Non signé'
              }
            </td>
          </tr>
          <tr>
            <td className="border border-black px-1 py-1 font-bold">Rapporteur</td>
            <td className="border border-black px-1 py-1">
              {juryInfo?.rapporteur ? 
                `${juryInfo.rapporteur.prenom} ${juryInfo.rapporteur.nom}` : 
                'Non assigné'
              }
            </td>
            <td className="border border-black px-1 py-1 text-center">
              {procesVerbal?.date_signature_rapporteur ? 
                `Signé le ${formatDate(procesVerbal.date_signature_rapporteur)}` : 
                'Non signé'
              }
            </td>
          </tr>
          <tr>
            <td className="border border-black px-1 py-1 font-bold">Examinateur</td>
            <td className="border border-black px-1 py-1">
              {juryInfo?.examinateur ? 
                `${juryInfo.examinateur.prenom} ${juryInfo.examinateur.nom}` : 
                'Non assigné'
              }
            </td>
            <td className="border border-black px-1 py-1 text-center">
              {procesVerbal?.date_signature_examinateur ? 
                `Signé le ${formatDate(procesVerbal.date_signature_examinateur)}` : 
                'Non signé'
              }
            </td>
          </tr>
        </tbody>
      </table>

      {/* Résultats de l'évaluation - Ultra compact */}
      <table className="w-full border-collapse mb-2" style={{fontSize: '10pt'}}>
        <thead>
          <tr style={{backgroundColor: '#e5e5e5'}}>
            <th className="border border-black px-1 py-1 font-bold text-left" colSpan="4">
              RÉSULTATS DE L'ÉVALUATION
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-black px-1 py-1 font-bold" style={{width: '20%'}}>Note finale</td>
            <td className="border border-black px-1 py-1 text-center font-bold" style={{width: '20%', fontSize: '12pt'}}>
              {procesVerbal?.note_finale ? `${procesVerbal.note_finale}/20` : 'Non attribuée'}
            </td>
            <td className="border border-black px-1 py-1 font-bold" style={{width: '20%'}}>Mention</td>
            <td className="border border-black px-1 py-1 text-center font-bold" style={{width: '40%'}}>
              {getMentionLabel(procesVerbal?.mention)}
            </td>
          </tr>
          <tr>
            <td className="border border-black px-1 py-1 font-bold">Décision du jury</td>
            <td className="border border-black px-1 py-1 text-center font-bold" colSpan="3" style={{fontSize: '11pt'}}>
              {getDecisionLabel(procesVerbal?.decision)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Observations et recommandations - Compact */}
      {(procesVerbal?.observations || procesVerbal?.recommandations) && (
        <div className="mb-2">
          <div className="border-2 border-black">
            <div className="bg-gray-200 px-1 py-1 font-bold text-xs border-b border-black">
              OBSERVATIONS ET RECOMMANDATIONS DU JURY
            </div>
            
            {procesVerbal?.observations && (
              <div className="px-1 py-1 border-b border-black">
                <div className="font-bold text-xs mb-1">Observations :</div>
                <div className="text-xs">{procesVerbal.observations}</div>
              </div>
            )}
            
            {procesVerbal?.recommandations && (
              <div className="px-1 py-1">
                <div className="font-bold text-xs mb-1">Recommandations :</div>
                <div className="text-xs">{procesVerbal.recommandations}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signatures - Ultra compact */}
      <table className="w-full border-collapse" style={{fontSize: '9pt'}}>
        <thead>
          <tr style={{backgroundColor: '#e5e5e5'}}>
            <th className="border border-black px-1 py-1 font-bold text-left" colSpan="3">
              SIGNATURES DU JURY
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-black px-1 py-1 text-center font-bold" style={{width: '33.33%'}}>
              PRÉSIDENT<br />
              {juryInfo?.president ? 
                `${juryInfo.president.prenom} ${juryInfo.president.nom}` : 
                'Non assigné'
              }<br />
              <div style={{height: '30px', marginTop: '5px'}}></div>
              {procesVerbal?.date_signature_president && (
                <div className="text-xs">Signé le {formatDate(procesVerbal.date_signature_president)}</div>
              )}
            </td>
            <td className="border border-black px-1 py-1 text-center font-bold" style={{width: '33.33%'}}>
              RAPPORTEUR<br />
              {juryInfo?.rapporteur ? 
                `${juryInfo.rapporteur.prenom} ${juryInfo.rapporteur.nom}` : 
                'Non assigné'
              }<br />
              <div style={{height: '30px', marginTop: '5px'}}></div>
              {procesVerbal?.date_signature_rapporteur && (
                <div className="text-xs">Signé le {formatDate(procesVerbal.date_signature_rapporteur)}</div>
              )}
            </td>
            <td className="border border-black px-1 py-1 text-center font-bold" style={{width: '33.33%'}}>
              EXAMINATEUR<br />
              {juryInfo?.examinateur ? 
                `${juryInfo.examinateur.prenom} ${juryInfo.examinateur.nom}` : 
                'Non assigné'
              }<br />
              <div style={{height: '30px', marginTop: '5px'}}></div>
              {procesVerbal?.date_signature_examinateur && (
                <div className="text-xs">Signé le {formatDate(procesVerbal.date_signature_examinateur)}</div>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default PrintContentProcesVerbal; 